import React from "react";
import { apiRequest } from "@/utils/apiHelper";
import { buildQueryString } from "@/utils/buildQueryString";
import { collegeLandingPageQueryParams } from "@/utils/collegeLandingPageQuery/collegeLandingPageQueryParams";
import PageDetailsTab from "@/src/Components/ui/common/pageTabDetail/PageTabDetail";
import { seoClgAndExamPageUtils } from "@/src/utils/seo-utils/seoClgAndExamPageUtils";
import { useGlobalContext } from "@/src/context/GlobalContext";
import { filterIsAbroadCountry } from "@/utils/helpers/filterItems";
import { HomeLayouts } from "@/src/Layouts";
import HeroSection from "@/src/Components/collegeComponents/HeroSection";
import PageNavbar from "@/src/Components/collegeComponents/CollegeNavbar";
import { domain_url } from "@/src/api/apiConstant";
import Head from "next/head";
import { generateNumberBasedOnId } from "@/utils/helpers/generateCount";
import {
  generateAggregateRatingSchema,
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateWebPageSchema,
} from "@/utils/schema/schema";
import SEOHead from "@/src/Components/seo/BlogSeoHead";
import {
  getCollegeLandingCache,
  setCollegeLandingCache,
} from "@/utils/collegeLandingPageQuery/collegeLandingDataCache";

const CollegeLandingPage = ({
  collegeData,
  collegeId,
  seoData,
  tab,
  isOnlineCollege,
  pageData,
}: any) => {
  const { setIsLeadFormOpen, setLeadFormData } = useGlobalContext();
  const pathValue = tab ?? "college-info";
  const navbar = collegeData?.college_navbars;
  const isAbroadCollege = filterIsAbroadCountry(collegeData);
  const college_title = seoData?.[0]?.main_title ?? collegeData?.college_title;
  const rating = generateNumberBasedOnId(collegeData?.id, 4, 5);
  const reviews = generateNumberBasedOnId(collegeData?.id, 500, 2000);

  const college_url = isOnlineCollege
    ? `${domain_url}/colleges/${collegeData?.college_url}`
    : `${domain_url}/colleges/${collegeData?.college_url}/${tab}`;

  const handleOpen = (
    collegeId: any,
    collegeName: any,
    isAbroadCountry: boolean
  ) => {
    setLeadFormData({
      source: `collegePage`,
      college: collegeId || null,
      title: collegeName || null,
      isAbroadCountry: isAbroadCountry ?? false,
    });
    setIsLeadFormOpen(true);
  };

  // const handleScroll = () => {
  //   if (window.scrollY > 200) {
  //     setTimeout(() => {
  //       window.scrollTo({
  //         top: 300,
  //         behavior: "smooth",
  //       });
  //     }, 100);
  //   }
  // };
  const seo = seoClgAndExamPageUtils(
    seoData,
    collegeData?.college_name,
    collegeId,
    "colleges",
    tab,
    isOnlineCollege,
    collegeData?.logo?.url,
    collegeData?.college_logo?.name || collegeData?.college_name,
    collegeData?.updatedAt,
    collegeData?.publishedAt
  );

  const title = collegeData?.abbreviation ?? collegeData?.college_name;
  const author_name =
    collegeData?.data?.updated_by_user?.name ||
    collegeData?.created_by_user?.name;

  const webPageSchema = generateWebPageSchema(title, college_url);
  const faqData = pageData?.find(
    (data: any) => data?.__component?.trim() === "common.faq"
  );
  const faqSchema = generateFAQSchema(faqData);
  const breadcrumbItems = generateBreadcrumbSchema(
    collegeData,
    isAbroadCollege,
    domain_url,
    college_url
  );

  const aggregateRatingSchema = generateAggregateRatingSchema(
    title,
    college_url,
    rating,
    `${reviews}`
  );
  const articleSchema = generateArticleSchema(
    title,
    domain_url,
    college_url,
    author_name,
    collegeData?.updatedAt,
    collegeData?.publishedAt,
    collegeData?.logo?.url
  );

  return (
    <>
      <SEOHead seoData={seo} />
      {domain_url === "https://collegehai.com" && collegeData?.college_url && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(aggregateRatingSchema),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebPage",
                speakable: {
                  "@type": "SpeakableSpecification",
                  xpath: [
                    "/html/head/title",
                    "/html/body//h1",
                    "/html/body//p[1]",
                  ],
                },
                url: college_url,
              }),
            }}
          />
        </Head>
      )}
      {breadcrumbItems.length > 0 && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems,
              }),
            }}
          />
        </Head>
      )}
      {faqSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        </Head>
      )}
      {articleSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
        </Head>
      )}

      <HomeLayouts>
        <HeroSection
          collegeData={collegeData}
          college_title={college_title}
          handleOpen={handleOpen}
          isAbroadCollege={isAbroadCollege}
          breadcrumbItems={breadcrumbItems}
        />

        {/* Apply Now Button for Mobile */}
        <div className="flex md:hidden justify-between pr-2 py-2 md:py-4 w-full gap-4 text-sm font-inter mt-2">
          <button
            className="bg-[#050138] text-white w-full py-2 rounded-md duration-300"
            onClick={() =>
              handleOpen(
                collegeData?.id,
                collegeData?.abbreviation,
                isAbroadCollege
              )
            }
          >
            Apply Now
          </button>
        </div>

        {/* Navbar Tabs */}
        {!isOnlineCollege && (
          <PageNavbar
            entity="colleges"
            navbar={navbar}
            url={collegeId}
            pathValue={pathValue}
          />
        )}

        {/* Tab Content */}
        <section className="mainSection w-full lg:max-w-screen-xl lg:mx-auto min-h-96">
          <PageDetailsTab
            tabsData={pageData}
            page={collegeData}
            currentTab={pathValue}
            loading={false}
          />
        </section>
      </HomeLayouts>
    </>
  );
};

export default CollegeLandingPage;

export async function getServerSideProps(context: any) {
  const { slug } = context.params;

  if (!slug || slug.length === 0) {
    return { notFound: true };
  }

  const collegeId = slug[0];
  const tab = slug[1] || null;

  const collegeCacheKey = JSON.stringify({
    ...collegeLandingPageQueryParams,
    collegeId,
  });

  let collegeData: any = getCollegeLandingCache(collegeCacheKey);

  if (!collegeData) {
    console.log("[Cache Miss] Fetching college data from API...");

    const queryParams = {
      ...collegeLandingPageQueryParams,
      filters: { college_url: { $eq: collegeId } },
    };

    const response = await apiRequest(
      `/colleges?${buildQueryString(queryParams)}`,
      "GET"
    );

    if (!response?.data?.data?.results?.[0]) {
      console.warn("[API Error] College not found.");
      return { notFound: true };
    }

    collegeData = response.data.data.results[0];
    setCollegeLandingCache(collegeCacheKey, collegeData);
  } else {
    console.log("[Cache Hit] College data retrieved from cache.");
  }

  const { page_data, ...collegeInfo } = collegeData;
  const isOnlineCollege = collegeInfo?.is_online_college;

  // Block rules
  if ((isOnlineCollege && tab) || (!isOnlineCollege && !tab)) {
    return { notFound: true };
  }

  const navbar = collegeInfo?.college_navbars?.[0]?.title;

  const seoData = isOnlineCollege
    ? page_data?.filter(
        (data: any) =>
          data?.college_navbar?.title === navbar &&
          data?.__component.trim() === "common.seo"
      )
    : page_data?.filter(
        (data: any) =>
          data?.college_navbar?.title
            ?.trim()
            .replace(/\s+/g, "-")
            .toLowerCase() === tab && data?.__component.trim() === "common.seo"
      );

  const pageData = isOnlineCollege
    ? page_data?.filter(
        (data: any) =>
          data?.college_navbar?.title === navbar &&
          data?.__component.trim() !== "common.seo"
      )
    : page_data?.filter(
        (data: any) =>
          data?.college_navbar?.title
            ?.trim()
            .replace(/\s+/g, "-")
            .toLowerCase() === tab && data?.__component.trim() !== "common.seo"
      );

  return {
    props: {
      collegeData: collegeInfo,
      collegeId,
      seoData,
      tab,
      isOnlineCollege,
      pageData,
    },
  };
}
